import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { combineLatest, Observable, of } from 'rxjs'
import * as R from 'ramda'
import { map } from 'rxjs/operators'
import { Store } from '@ngrx/store'
import {
  InvoiceRule,
  InvoiceRuleCreateRequest,
  InvoiceRuleLog,
  InvoiceRuleLogView,
  InvoiceRuleUpdateRequest,
  RuleConditionModel,
} from './rule.model'
import { selectAllInvoiceTemplates } from '../templates/template.selectors'
import { InvoiceTemplate } from '../templates/template.model'
import { epochToView, isSomething, selectOnce } from '@tokeet/tv3-platform'
import { TriggerEvents, TruncatePipe } from '@tokeet-frontend/utility'

@Injectable()
export class InvoiceRuleService {
  constructor(private http: HttpClient, private store: Store<any>) {}

  logs(ruleId: string): Observable<InvoiceRuleLogView[]> {
    const url = `@api/notification/invoicing/?limit=10000&skip=0`

    return combineLatest(
      this.http.get<InvoiceRuleLog[]>(url),
      this.store.pipe(selectOnce(selectAllInvoiceTemplates))
    ).pipe(
      map(([logs, templates]) =>
        R.map((log) => {
          const invoiceTemplate = R.find((t: InvoiceTemplate) => t.pkey === log.template, templates)
          let templateView = ''
          if (isSomething(invoiceTemplate)) {
            templateView = new TruncatePipe().transform(invoiceTemplate.name, 40)
          }
          let eventView = TriggerEvents.getEventName(log.event)
          const sentView = epochToView(log.sent, 'DD - MMM - YYYY | h:mm A')
          const channelView = log.channel_name === 'tokeet' || log.channel_name === 'sympl' ? 'Sympl' : log.channel_name
          return {
            ...log,
            sentView,
            templateView,
            eventView,
            channelView,
          }
        }, logs)
      ),
      map((logs) => R.filter((l) => l.trigger === ruleId, logs))
    )
  }

  all() {
    const url = `@api/invoicerule/all/`

    return this.http
      .get<InvoiceRule[]>(url)
      .pipe(map((rules) => R.map((rule: InvoiceRule) => this.convertAirBnB(rule), rules)))
  }

  pauseRuleReminder(id: string) {
    const url = `@api/invoicerule/reminder/pause/${id}`

    return this.http.put<InvoiceRule>(url, {}).pipe(map((rule) => this.convertAirBnB(rule)))
  }

  resumeRuleReminder(id: string, when: 'time.before.invoice.due' | 'time.after.invoice.due', days: number) {
    const url = `@api/invoicerule/reminder/resume/${id}`

    return this.http
      .put<InvoiceRule>(url, { when, days })
      .pipe(map((rule) => this.convertAirBnB(rule)))
  }

  createMany(payload): Observable<InvoiceRule[]> {
    return this.http
      .post<InvoiceRule[]>(`@api/invoicerules/`, payload)
      .pipe(map((rules) => R.map((rule: InvoiceRule) => this.convertAirBnB(rule), rules)))
  }

  create(request: InvoiceRuleCreateRequest): Observable<InvoiceRule> {
    return this.http.post<InvoiceRule>(`@api/invoicerule/`, request).pipe(map((rule) => this.convertAirBnB(rule)))
  }

  update(request: InvoiceRuleUpdateRequest): Observable<InvoiceRule> {
    return this.http
      .put<InvoiceRule>(`@api/invoicerule/${request.pkey}`, request)
      .pipe(map((rule) => this.convertAirBnB(rule)))
  }

  get(pkey): Observable<InvoiceRule> {
    return this.http.get<InvoiceRule>(`@api/invoicerule/${pkey}`).pipe(map((rule) => this.convertAirBnB(rule)))
  }

  pause(pkey): Observable<InvoiceRule> {
    return this.http
      .put<InvoiceRule>(`@api/invoicerule/pause/${pkey}`, {})
      .pipe(map((rule) => this.convertAirBnB(rule)))
  }

  resume(pkey): Observable<InvoiceRule> {
    return this.http
      .put<InvoiceRule>(`@api/invoicerule/resume/${pkey}`, {})
      .pipe(map((rule) => this.convertAirBnB(rule)))
  }

  archive(pkey): Observable<InvoiceRule> {
    return this.http
      .put<InvoiceRule>(`@api/invoicerule/archive/${pkey}`, {})
      .pipe(map((rule) => this.convertAirBnB(rule)))
  }

  unarchive(pkey): Observable<InvoiceRule> {
    return this.http
      .put<InvoiceRule>(`@api/invoicerule/unarchive/${pkey}`, {})
      .pipe(map((rule) => this.convertAirBnB(rule)))
  }

  delete(pkey) {
    return this.http.delete(`@api/invoicerule/${pkey}`)
  }

  deleteBatch(ids: string[]): Observable<string[]> {
    if (R.isEmpty(ids)) {
      return of([])
    }
    return this.http.put<string[]>(`@api/invoicerule/delete/batch`, { ids: ids })
  }

  saveCondition(request) {
    return this.http.put<RuleConditionModel>(`@api/invoicerule/condition/${request.ruleId}`, {
      ...request.condition,
      key: request.conditionId,
    })
  }

  createCondition(request) {
    return this.http.post<RuleConditionModel>(`@api/invoicerule/condition/${request.ruleId}`, request.condition)
  }

  deleteCondition(request) {
    return this.http.put(`@api/invoicerule/condition/remove/${request.ruleId}`, { key: request.conditionId })
  }

  private convertAirBnB(rule: InvoiceRule) {
    const channel = R.path(['settings', 'channel'], rule)
    if (channel === 'airbnb') {
      return { ...rule, settings: { ...rule.settings, channel: 'airbnbapiv2' } }
    } else {
      return rule
    }
  }
}
